import { StringFormatter } from '@repo/toolkit/string'
import { InputHTMLAttributes, ReactNode } from 'react'

import { InputWrapperProps } from './input-wrapper'
import NumberInput, { NumberInputProps } from './number-input'
import PasswordInput from './password-input'
import TextInput from './text-input'

export type PlaceholderVariant = 'dark' | 'light'
interface BaseInputProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'children' | 'type' | 'onClick'>,
    Omit<InputWrapperProps, 'children'> {}

export type InputProps = Omit<BaseInputProps, 'value'> & {
  inputClassName?: string
  options?: { clear: boolean }
  value?: string | number | null
  suffix?: string | React.ReactNode
  prefix?: string | React.ReactNode
  placeholderVariant?: PlaceholderVariant
  required?: boolean
  /**
   * Tooltip content
   */
  tooltipContent?: string | ReactNode
} & (
    | { type: 'text' | 'email' | 'password' | 'time' }
    | ({ type: 'number' } & NumberInputProps)
    | { type: 'textarea'; rows?: number }
  )

const Input: React.FC<InputProps> = ({
  type,
  required,
  label,
  tooltipContent,
  value,
  ...props
}) => {
  const labelWithRequired = StringFormatter.isRequired(label ?? '', required)
  const normalizedValue = value ?? undefined

  switch (type) {
    case 'password':
      return <PasswordInput {...props} label={labelWithRequired} value={normalizedValue} />
    case 'text':
    case 'textarea':
    case 'email':
      return (
        <TextInput
          {...props}
          type={type}
          label={labelWithRequired}
          value={normalizedValue}
          tooltipContent={tooltipContent}
        />
      )
    case 'number':
      return <NumberInput {...props} label={labelWithRequired} value={normalizedValue} />
    default:
      return (
        <TextInput
          {...props}
          type={type}
          label={labelWithRequired}
          value={normalizedValue}
          tooltipContent={tooltipContent}
        />
      )
  }
}

export default Input
